import { useState } from 'react'
import { useAuth } from './useAuth'

// ── BIBLE READING SETTINGS (redesign Phase 6) ───────────────────────────
// Text size (5 steps) + dyslexia-friendly font, persisted per user in
// localStorage. Exposes a ready-to-spread `cssVars` object — the consumer
// (BibleApp.jsx) spreads it onto `.app`'s inline style alongside the
// existing `--accent` variable, so any scripture-text rule anywhere in the
// tree can reference `var(--scripture-font/size/lh)` without prop-drilling.

const SIZES = [16, 18, 20, 23, 26] // px, index 2 (20px) is the default
const SERIF_STACK = "'Source Serif 4', Georgia, serif"
const DYSLEXIC_STACK = "'OpenDyslexic', 'Plus Jakarta Sans', sans-serif"

function userScopedKey(base, userId) {
  return userId ? `${base}:${userId}` : base
}
function readUserScoped(base, userId) {
  if (typeof window === 'undefined' || !userId) return null
  try { return localStorage.getItem(userScopedKey(base, userId)) }
  catch (_e) { return null }
}
function writeUserScoped(base, userId, value) {
  if (typeof window === 'undefined' || !userId) return
  try { localStorage.setItem(userScopedKey(base, userId), value) }
  catch (_e) { /* ignore quota */ }
}

export function useReadingSettings() {
  const { user } = useAuth()
  const userId = user?.id

  const [sizeIndex, setSizeIndexState] = useState(() => {
    const raw = readUserScoped('nlc.readingSize', userId)
    const n = raw != null ? parseInt(raw, 10) : NaN
    return Number.isFinite(n) && n >= 0 && n < SIZES.length ? n : 2
  })
  const [dyslexic, setDyslexicState] = useState(() => {
    return readUserScoped('nlc.readingDyslexic', userId) === '1'
  })

  function setSizeIndex(next) {
    const clamped = Math.max(0, Math.min(SIZES.length - 1, next))
    setSizeIndexState(clamped)
    writeUserScoped('nlc.readingSize', userId, String(clamped))
  }
  function setDyslexic(next) {
    setDyslexicState(next)
    writeUserScoped('nlc.readingDyslexic', userId, next ? '1' : '0')
  }

  const cssVars = {
    '--scripture-font': dyslexic ? DYSLEXIC_STACK : SERIF_STACK,
    '--scripture-size': `${SIZES[sizeIndex]}px`,
    '--scripture-lh': dyslexic ? 1.85 : 1.7,
  }

  return { sizeIndex, setSizeIndex, dyslexic, setDyslexic, sizes: SIZES, cssVars }
}
